import React from 'react'
import clsx from 'clsx'
import { Prompt } from 'react-router'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'
import CategoryTable from './CategoryTable'
import TaskModal from '@SonarCX/components/molecules/ChatModals/TaskModal'

import { CATEGORY_TAB } from '@SonarCX/providers/TaskPageStateProvider'
import { selectCategory, fetchCategories } from '@SonarCX/slices/category'
import { clearSelected } from '@SonarCX/slices/allTasksTable'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useTaskSWRMutateCache } from '@SonarCX/providers/TaskSWRMutateCache'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import useBaseStyles from '../styles'
import useStyles from './styles'

type CategoryTabProps = {
  pageRef: React.MutableRefObject<HTMLDivElement>
}

const CategoryTab: React.FC<CategoryTabProps> = ({ pageRef }) => {
  const dispatch = useAppDispatch()

  const {
    categoriesValues: categories,
    loaded: loadedCategories,
    loading: loadingCategories
  } = useAppSelector(selectCategory)

  const { isSmall } = useBreakpoint()
  const { updateTasks } = useTaskSWRMutateCache()
  const { env, loaded } = useEnvironment()

  const [taskModalOpen, setTaskModalOpen] = React.useState(false)
  const [taskModalCategory, setTaskModalCategory] = React.useState('')

  const sortedCategories = React.useMemo(() => {
    const privateCats: APICategory[] = []
    const publicCats: APICategory[] = []

    categories.forEach(category => {
      if (category.private) {
        privateCats.push(category)
      } else {
        publicCats.push(category)
      }
    })

    return {
      private: privateCats,
      public: publicCats
    }
  }, [categories])
  const envName = React.useMemo(() => {
    const name = loaded ? env?.name ?? '' : ''

    if (!name) {
      return ''
    }

    const justName = name.split('(')[0].trim()

    return `${justName} Categories`
  }, [loaded, env])

  const baseClasses = useBaseStyles({ smallMedia: isSmall })
  const classes = useStyles()

  const createTask = React.useCallback(() => setTaskModalOpen(true), [])

  React.useEffect(() => {
    if (!loadedCategories && !loadingCategories) {
      dispatch(fetchCategories())
    }
  }, [loadedCategories, loadingCategories, dispatch])

  if (!loadedCategories) {
    return <LoadingSpinner />
  }

  return (
    <>
      <div className={baseClasses.root}>
        <div className={clsx(classes.block, classes.blockOne)}>
          <div className={classes.header}>Your Categories</div>

          <div className={baseClasses.tasks}>
            {sortedCategories.private.map(category => (
              // @ts-ignore
              <CategoryTable
                key={category.id}
                pageRef={pageRef}
                category={category}
                createTask={createTask}
                setTaskModalCategory={setTaskModalCategory}
              />
            ))}
          </div>
        </div>

        <div className={clsx(classes.block, classes.blockTwo)}>
          <div className={classes.header}>{envName}</div>

          <div className={baseClasses.tasks}>
            {sortedCategories.public.map(category => (
              // @ts-ignore
              <CategoryTable
                key={category.id}
                pageRef={pageRef}
                category={category}
                createTask={createTask}
                setTaskModalCategory={setTaskModalCategory}
              />
            ))}
          </div>
        </div>

        <div className={clsx(classes.block, classes.noBlock)}>
          <div className={baseClasses.tasks}>
            {/* @ts-ignore */}
            <CategoryTable
              pageRef={pageRef}
              createTask={createTask}
              isNone
              hideHeader
            />
          </div>
        </div>

        {taskModalOpen && (
          // @ts-ignore
          <TaskModal
            open={taskModalOpen}
            onClose={() => setTaskModalOpen(false)}
            onTaskCreated={task => updateTasks(CATEGORY_TAB, task)}
            canEdit
            canUpdate
            selectedCategory={taskModalCategory}
          />
        )}
      </div>

      <Prompt
        when
        message={() => {
          dispatch(clearSelected())
          return true
        }}
      />
    </>
  )
}

export default CategoryTab
